import { Injectable, inject } from '@angular/core';
import { SocketService } from './socket.service';
import { WopiServerStateStoreService, ResponseType, HttpMethod } from './state-store/wopi-server-state-store/wopi-server-state-store.service';
import {
  SocketEvents,
  GetFileEntityRequest,
  GetFileEntityResponse,
  GetBizEntityRequest,
  GetBizEntityResponse,
  UploadFileRequest,
  UploadFileResponse,
  DownloadFileRequest,
  DownloadFileResponse,
  GetFileMetadataRequest,
  GetFileMetadataResponse,
} from '../../types/socket-events.types';
import { BizEntityStateStoreService, FileStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { Html2CanvasThumbnailHelper } from '../shared/utils/html2canvas-thumbnail.helper';
import { EntityLayoutConfigService } from './entity-layout-config.service';

/**
 * WOPI API Proxy Service
 * 
 * This service acts as a proxy for external API calls requested by the WOPI server.
 * Instead of the WOPI server making external API calls directly, it requests them
 * via WebSocket, and this service executes them and sends back the responses.
 * 
 * This architecture provides:
 * - Better scalability (offloads API calls from server)
 * - No CORS issues (calls are made from browser)
 * - Client-side credentials/headers
 * - Support for 5000+ concurrent users
 * 
 * CRITICAL CHANGE: Listeners are now file-specific, not global.
 * Each file viewer component must call initializeForFile() and cleanupForFile()
 */
@Injectable({
  providedIn: 'root'
})
export class WopiApiProxyService {
  private fileStateStoreService = inject(FileStateStoreService);
  private bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private socketService = inject(SocketService);
  private entityLayoutConfigService = inject(EntityLayoutConfigService);
  private wopiServerStateStoreService = inject(WopiServerStateStoreService);

  // Track listeners per file to allow proper cleanup
  private fileListeners = new Map<string, {
    getFileEntity: (request: GetFileEntityRequest) => void;
    getBizEntity: (request: GetBizEntityRequest) => void;
    uploadFile: (request: UploadFileRequest) => void;
    downloadFile: (request: DownloadFileRequest) => void;
    getFileMetadata: (request: GetFileMetadataRequest) => void;
  }>();

  /**
   * Initialize the API proxy service for a specific file
   * Sets up listeners for all API request events from the WOPI server
   * 
   * CRITICAL: Call this when a file viewer component initializes
   * CRITICAL: Must call cleanupForFile() in ngOnDestroy to prevent memory leaks
   * 
   * @param fileId - The unique file identifier (bizEntityId)
   */
  initializeForFile(fileId: string): void {
    // Prevent duplicate initialization
    if (this.fileListeners.has(fileId)) {
      return;
    }

    // Create bound handlers for this file
    const handlers = {
      getFileEntity: (request: GetFileEntityRequest) => {
        // Only handle requests for this specific file
        // CRITICAL: Normalize both to strings for type-safe comparison
        const requestBizEntityId = String(request.bizEntityId);
        const normalizedFileId = String(fileId);
        
        if (requestBizEntityId === normalizedFileId) {
          this.handleGetFileEntity(request);
        }
      },
      getBizEntity: (request: GetBizEntityRequest) => {
        // CRITICAL: Normalize both to strings for type-safe comparison
        const requestBizEntityId = String(request.bizEntityId);
        const normalizedFileId = String(fileId);
        
        if (requestBizEntityId === normalizedFileId) {
          this.handleGetBizEntity(request);
        }
      },
      uploadFile: (request: UploadFileRequest) => {
        // CRITICAL: Normalize both to strings for type-safe comparison
        const requestBizEntityId = String(request.bizEntityId);
        const normalizedFileId = String(fileId);
        
        if (requestBizEntityId === normalizedFileId) {
          this.handleUploadFile(request);
        }
      },
      downloadFile: (request: DownloadFileRequest) => {
        // If bizEntityId is provided, check if it matches this file
        if (request.bizEntityId) {
          const requestBizEntityId = String(request.bizEntityId);
          const normalizedFileId = String(fileId);
          
          if (requestBizEntityId === normalizedFileId) {
            this.handleDownloadFile(request);
          }
        } else {
          // Legacy behavior: no bizEntityId means handle it (for backwards compatibility)
          this.handleDownloadFile(request);
        }
      },
      getFileMetadata: (request: GetFileMetadataRequest) => {
        // If bizEntityId is provided, check if it matches this file
        if (request.bizEntityId) {
          const requestBizEntityId = String(request.bizEntityId);
          const normalizedFileId = String(fileId);
          
          if (requestBizEntityId === normalizedFileId) {
            this.handleGetFileMetadata(request);
          }
        } else {
          // Legacy behavior: no bizEntityId means handle it (for backwards compatibility)
          this.handleGetFileMetadata(request);
        }
      },
    };

    // Store handlers for cleanup
    this.fileListeners.set(fileId, handlers);

    // Register listeners with socket service
    this.socketService.on<GetFileEntityRequest>(
      SocketEvents.GET_FILE_ENTITY,
      handlers.getFileEntity
    );

    this.socketService.on<GetBizEntityRequest>(
      SocketEvents.GET_BIZ_ENTITY,
      handlers.getBizEntity
    );

    this.socketService.on<UploadFileRequest>(
      SocketEvents.UPLOAD_FILE,
      handlers.uploadFile
    );

    this.socketService.on<DownloadFileRequest>(
      SocketEvents.DOWNLOAD_FILE,
      handlers.downloadFile
    );

    this.socketService.on<GetFileMetadataRequest>(
      SocketEvents.GET_FILE_METADATA,
      handlers.getFileMetadata
    );
  }

  /**
   * Clean up listeners for a specific file
   * 
   * CRITICAL: Call this in ngOnDestroy to prevent:
   * - Memory leaks
   * - Duplicate event handlers
   * - Cross-file event pollution
   * 
   * @param fileId - The unique file identifier (bizEntityId)
   */
  cleanupForFile(fileId: string): void {
    const handlers = this.fileListeners.get(fileId);
    
    if (!handlers) {
      return;
    }

    // Remove all event listeners
    this.socketService.off(SocketEvents.GET_FILE_ENTITY, handlers.getFileEntity);
    this.socketService.off(SocketEvents.GET_BIZ_ENTITY, handlers.getBizEntity);
    this.socketService.off(SocketEvents.UPLOAD_FILE, handlers.uploadFile);
    this.socketService.off(SocketEvents.DOWNLOAD_FILE, handlers.downloadFile);
    this.socketService.off(SocketEvents.GET_FILE_METADATA, handlers.getFileMetadata);

    // Remove from tracking
    this.fileListeners.delete(fileId);
  }

  /**
   * @deprecated Use initializeForFile(fileId) instead
   * 
   * This global initialization method is DEPRECATED because it causes
   * cross-file event pollution. Each file viewer should call initializeForFile()
   * with its specific fileId.
   */
  initialize(): void {

    // Listen for file entity requests
    this.socketService.on<GetFileEntityRequest>(
      SocketEvents.GET_FILE_ENTITY,
      (request) => this.handleGetFileEntity(request)
    );

    // Listen for biz entity requests
    this.socketService.on<GetBizEntityRequest>(
      SocketEvents.GET_BIZ_ENTITY,
      (request) => this.handleGetBizEntity(request)
    );

    // Listen for upload file requests
    this.socketService.on<UploadFileRequest>(
      SocketEvents.UPLOAD_FILE,
      (request) => this.handleUploadFile(request)
    );

    // Listen for download file requests
    this.socketService.on<DownloadFileRequest>(
      SocketEvents.DOWNLOAD_FILE,
      (request) => this.handleDownloadFile(request)
    );

    // Listen for file metadata requests

    this.socketService.on<GetFileMetadataRequest>(
      SocketEvents.GET_FILE_METADATA,
      (request) => this.handleGetFileMetadata(request)
    );
  }

  /**
   * Handle get file entity request
   * Uses FileStateStoreService instead of direct HTTP calls
   */
  private async handleGetFileEntity(request: GetFileEntityRequest): Promise<void> {
    const response: GetFileEntityResponse = {
      requestId: request.requestId,
      success: false,
      timestamp: Date.now(),
    };

    try {
      // Use FileStateStoreService to get file entity metadata
      const apiResponse = await this.fileStateStoreService.getState([request.bizEntityId]);
      const entity = Array.isArray(apiResponse) ? apiResponse[0] : apiResponse;

      response.success = true;
      response.data = entity || null;
    } catch (error: any) {
      response.error = error.message || 'Failed to get file entity';
    }

    // Send response back to server
    this.socketService.emit(SocketEvents.GET_FILE_ENTITY_RESPONSE, response);
  }

  /**
   * Handle get biz entity request
   * Uses BizEntityStateStoreService instead of direct HTTP calls
   */
  private async handleGetBizEntity(request: GetBizEntityRequest): Promise<void> {
     const response: GetBizEntityResponse = {
      requestId: request.requestId,
      success: false,
      timestamp: Date.now(),
    };

    try {
      // Use BizEntityStateStoreService to get biz entity
      const apiResponse = await this.bizEntityStateStoreService.getState(
        this.bizEntityTypeStateStoreService,
        undefined,
        [request.bizEntityId], // ids - get specific entity
        undefined, // parentId
        undefined, // getAncestorsForBizEntityId
        undefined, // typeIds
        undefined, // categoryTypes
        undefined, // onlyDraft
        undefined  // includeDraft
      );

      const entity = Array.isArray(apiResponse) && apiResponse.length > 0 ? apiResponse[0] : null;

      response.success = true;
      response.data = entity;

    } catch (error: any) {
      response.error = error.message || 'Failed to get biz entity';
    }

    // Send response back to server
    this.socketService.emit(SocketEvents.GET_BIZ_ENTITY_RESPONSE, response);
  }

    private base64ToFile(base64: string, fileName: string): File {
    if (!base64) {
      throw new Error('Base64 string is empty or undefined');
    }

    // Determine MIME type from filename
    const extension = fileName.toLowerCase().split('.').pop() || '';
    const mimeTypes: Record<string, string> = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp'
    };
    const mime = mimeTypes[extension] || 'image/jpeg'; // Default to JPEG for thumbnails

    // If base64 doesn't have data URL prefix, add it
    let base64Data = base64;
    if (!base64.includes(',')) {
      // Raw base64 without data URL prefix - add it
      base64Data = `data:${mime};base64,${base64}`;
    }

    const arr = base64Data.split(',');
    if (arr.length < 2) {
      throw new Error('Invalid base64 format: incomplete data after split');
    }

    // Decode base64 to binary
    try {
      const bstr = atob(arr[1]);
      
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) {
        u8arr[i] = bstr.charCodeAt(i);
      }

      const file = new File([u8arr], fileName, { type: mime });
      
      // Verify first few bytes (JPEG starts with FF D8, PNG with 89 50)
      const firstBytes = Array.from(u8arr.slice(0, 4)).map(b => `0x${b.toString(16).padStart(2, '0')}`).join(' ');
      
      return file;
    } catch (error: any) {
      throw new Error(`Failed to decode base64 thumbnail: ${error.message}`);
    }
  }
  /**
   * Handle upload file request
   * Uses HeaderUtilityService and FormData (following markdown editor pattern)
   */
  private async handleUploadFile(request: UploadFileRequest): Promise<void> {
    
    // Validate thumbnail if present
    if (request.thumbnail) {      
      // Check if thumbnail looks valid
      if (request.thumbnail.length < 100) {
      }
    } else {
    }

    const response: UploadFileResponse = {
      requestId: request.requestId,
      success: false,
      timestamp: Date.now(),
    };

    try {
      // Convert base64 to Blob
      const byteCharacters = atob(request.fileData);
      
      // Verify size matches
      if (byteCharacters.length !== request.fileSize) {
      }
      
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Detect correct MIME type from file extension
      const mimeType = this.getMimeTypeFromFileName(request.fileName);
      
      const blob = new Blob([byteArray], { type: mimeType });

      // Convert Blob to File object (backend might expect File, not just Blob)
      const file = new File([blob], request.fileName, { type: mimeType });

      // Only create zip file with thumbnail for newly created files (0 bytes)
      // For Collabora saves (actual document content), upload the file directly
      const isNewFile = file.size === 0;
      
      let fileToUpload: File = file;
      let uploadFileName = request.fileName;
      
      if (isNewFile && request?.thumbnail && request.thumbnail.length > 0) {
        try {
          
          // Validate thumbnail base64 is not empty or corrupted
          if (request.thumbnail.length < 100) {
            throw new Error('Thumbnail too small, likely corrupted');
          }
          
          const imageFile = this.base64ToFile(
            request.thumbnail,
            `${request.fileName || 'document'}-thumb.jpg`
          );
          
          // Validate the image file is not too small
          if (imageFile.size < 100) {
            throw new Error('Converted thumbnail too small');
          }
          
          const zipBlob = await Html2CanvasThumbnailHelper.generateZipFile(imageFile, file);
          // Convert Blob to File for API compatibility
          fileToUpload = new File([zipBlob], 'files.zip', { type: zipBlob.type || 'application/zip' });
          uploadFileName = 'files.zip';
        } catch (thumbnailError: any) {
          // Fallback: upload file without thumbnail
          fileToUpload = file;
          uploadFileName = request.fileName;
        }
      } else {
        // Upload file directly (either no thumbnail, or file has actual content from Collabora)
        if (!isNewFile) {
                } else {
        }
        fileToUpload = file;
        uploadFileName = request.fileName;
      }
      // Use FileStateStoreService to update the file
      const apiResponse = await this.fileStateStoreService.update(fileToUpload, request.bizEntityId);
      response.success = true;
      response.data = apiResponse;
      
      // Clear the entity layout cache to force fresh thumbnail fetch
      // This ensures the thumbnail URL signal is recreated and fetches the updated thumbnail
      this.entityLayoutConfigService.clearEntityCache(request.bizEntityId);
    } catch (error: any) {
      
      // Log detailed error information
      if (error.error) {
      }
      if (error.status) {
      }
      if (error.statusText) {
      }
      
      response.error = error.message || 'Failed to upload file';
    }

    // Send response back to server
    this.socketService.emit(SocketEvents.UPLOAD_FILE_RESPONSE, response);
  }

  /**
   * Get MIME type from file name extension
   * Returns the correct MIME type for common file formats
   */
  private getMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.toLowerCase().split('.').pop() || '';
    
    const mimeTypes: Record<string, string> = {
      // Microsoft Office formats
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      
      // OpenDocument formats
      'odt': 'application/vnd.oasis.opendocument.text',
      'ods': 'application/vnd.oasis.opendocument.spreadsheet',
      'odp': 'application/vnd.oasis.opendocument.presentation',
      
      // Other document formats
      'pdf': 'application/pdf',
      'rtf': 'application/rtf',
      'txt': 'text/plain',
      'md': 'text/markdown',
      'csv': 'text/csv',
      
      // Archives
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed',
      'tar': 'application/x-tar',
      'gz': 'application/gzip',
      
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',
      'webp': 'image/webp',
      
      // Special formats
      'whiteboard': 'application/json',
      'drawing': 'application/json',
    };

    const mimeType = mimeTypes[extension];
    
    if (!mimeType) {
      return 'application/octet-stream';
    }
    
    return mimeType;
  }

  /**
   * Handle download file request
   */
  private async handleDownloadFile(request: DownloadFileRequest): Promise<void> {

    const response: DownloadFileResponse = {
      requestId: request.requestId,
      success: false,
      timestamp: Date.now(),
    };

    try {
      const apiResponse = await this.wopiServerStateStoreService.downloadFile<ArrayBuffer>(request.fileUrl, { 
        responseType: ResponseType.ARRAYBUFFER 
      });

      // Convert ArrayBuffer to base64
      const buffer = new Uint8Array(apiResponse);
      
      // Handle empty files (newly created documents)
      let base64 = '';
      if (buffer.byteLength > 0) {
        let binary = '';
        for (let i = 0; i < buffer.byteLength; i++) {
          binary += String.fromCharCode(buffer[i]);
        }
        base64 = btoa(binary);
      }

      response.success = true;
      response.data = base64; // Empty string for 0-byte files
      response.size = buffer.byteLength;
    } catch (error: any) {
      response.error = error.message || 'Failed to download file';
    }

    // Send response back to server
    this.socketService.emit(SocketEvents.DOWNLOAD_FILE_RESPONSE, response);
  }

  /**
   * Handle get file metadata request
   */
  private async handleGetFileMetadata(request: GetFileMetadataRequest): Promise<void> {
    const response: GetFileMetadataResponse = {
      requestId: request.requestId,
      success: false,
      timestamp: Date.now(),
    };

    try {
      // Strategy 1: Try GET with Range first (works with signed GET URLs)
      try {
        const rangeResponse = await this.wopiServerStateStoreService.getFileMetadata(request.fileUrl, { 
          method: HttpMethod.GET 
        });

        // Extract size from Content-Range header
        const contentRange = rangeResponse.headers.get('content-range');
        let size = 1;

        if (contentRange) {
          const match = contentRange.match(/bytes \d+-\d+\/(\d+)/);
          if (match) {
            size = parseInt(match[1]);
          }
        }

        const lastModified = rangeResponse.headers.get('last-modified') || new Date().toISOString();

        response.success = true;
        response.data = {
          size: size || 1,
          lastModified,
        };
      } catch (getError) {
        // Strategy 2: Try HEAD if GET fails (for legacy or non-S3 URLs)
        const headResponse = await this.wopiServerStateStoreService.getFileMetadata(request.fileUrl, { 
          method: HttpMethod.HEAD 
        });

        const contentLength = parseInt(headResponse.headers.get('content-length') || '0');
        const lastModified = headResponse.headers.get('last-modified') || new Date().toISOString();

        response.success = true;
        response.data = {
          size: contentLength || 1,
          lastModified,
        };
      }
    } catch (error: any) {
      // Return default values on error
      response.success = true;
      response.data = {
        size: 1,
        lastModified: new Date().toISOString(),
      };
    }

    // Send response back to server
    this.socketService.emit(SocketEvents.GET_FILE_METADATA_RESPONSE, response);
  }

  /**
   * Cleanup when service is destroyed
   */
  destroy(): void {

    // Remove all listeners
    this.socketService.off(SocketEvents.GET_FILE_ENTITY);
    this.socketService.off(SocketEvents.GET_BIZ_ENTITY);
    this.socketService.off(SocketEvents.UPLOAD_FILE);
    this.socketService.off(SocketEvents.DOWNLOAD_FILE);
    this.socketService.off(SocketEvents.GET_FILE_METADATA);
  }
}
